import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostJson } from './client'

/**
 * Settings → Easeedesk → Ticket timers, si Ticket priorities by type.
 *
 * Perechile vechi: `settings/easeedesk/ticket_type_timers`,
 * `update_ticket_type_timer`, `ticket_type_priorities`, `change_type_priority`,
 * `update_ticket_type_priority`.
 *
 * `apiGet` / `apiPost` pun ELE `/api/v2` in fata caii.
 */

export interface TicketTypeRow {
  ID: number
  type_name: string
  short_name: string
  color_identifier: string
  active: boolean
}

/** Cronometrele: minute, sau `null` cand tipul n-are inca un rand. */
export interface TicketTypeTimersRow extends TicketTypeRow {
  take_over: number | null
  identify: number | null
  intervention: number | null
}

export interface TicketTypePrioritiesRow extends TicketTypeRow {
  /** Cu ce prioritate porneste tipul. 0 = fara prioritate automata. */
  default_priority: number
  /** Ce poate alege agentul. Lista goala = toate cele active. */
  chosen: number[]
}

export interface PriorityRow {
  ID: number
  priority_name: string
  priority_color: string
}

export interface TicketTypeTimersPage {
  title: string
  moduleUrl: string
  types: TicketTypeTimersRow[]
}

export interface TicketTypePrioritiesPage {
  title: string
  moduleUrl: string
  types: TicketTypePrioritiesRow[]
  priorities: PriorityRow[]
  /** Prioritati setate candva si scoase intre timp din uz. */
  inactive: { ID: number; priority_name: string }[]
  /** A rulat migrarea coloanei `default_priority`? */
  migrated: boolean
  /** Exista tabela cu prioritatile permise pe tip? */
  chooseAvailable: boolean
}

/** 1 preluare, 2 identificare, 3 interventie - numerele din machete. */
export type TimerCategory = 1 | 2 | 3

export function ticketTypeTimersQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_type_timers'],
    queryFn: () => apiGet<TicketTypeTimersPage>('/settings/easeedesk/ticket_type_timers'),
  })
}

export function saveTicketTypeTimer(type: number, category: TimerCategory, time: number) {
  return apiPost<{ ok?: boolean }>('/settings/easeedesk/ticket_type_timers', { type, category, time })
}

export function ticketTypePrioritiesQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_type_priorities'],
    queryFn: () => apiGet<TicketTypePrioritiesPage>('/settings/easeedesk/ticket_type_priorities'),
  })
}

/** Bifa din coloana "Priorities the agent can pick". */
export function chooseTicketTypePriority(type: number, priority: number, chosen: boolean) {
  return apiPost<{ ok?: boolean; chosen?: number[] }>('/settings/easeedesk/ticket_type_priorities/choose', {
    type,
    priority,
    chosen: chosen ? 1 : 0,
  })
}

/** Prioritatea cu care porneste tipul. 0 = fara prioritate automata. */
export function setTicketTypeDefaultPriority(type: number, priority: number) {
  return apiPost<{ ok?: boolean }>('/settings/easeedesk/ticket_type_priorities', { type, priority })
}

/* -------------------------------------------------------------------------
 * Ticket types services
 *
 * Perechile vechi: `settings/easeedesk/ticket_types_services` si
 * `change_type_service`. Alt drept decat restul grupului:
 * `settings.easeedeskticket_services.read`.
 * ---------------------------------------------------------------------- */

export interface TicketTypeServicesRow {
  ID: number
  type_name: string
  short_name: string
  color_identifier: string
  /** Serviciile bifate pe tipul asta. */
  services: number[]
}

export interface ServiceRow {
  ID: number
  service_name: string
}

export interface TicketTypeServicesPage {
  title: string
  moduleUrl: string
  types: TicketTypeServicesRow[]
  services: ServiceRow[]
}

export function ticketTypeServicesQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_types_services'],
    queryFn: () => apiGet<TicketTypeServicesPage>('/settings/easeedesk/ticket_types_services'),
  })
}

export function chooseTicketTypeService(type: number, service: number, chosen: boolean) {
  return apiPost<{ ok?: boolean; services?: number[] }>('/settings/easeedesk/ticket_types_services', {
    type,
    service,
    chosen: chosen ? 1 : 0,
  })
}

/* -------------------------------------------------------------------------
 * Lista tipurilor de tichet
 *
 * Perechile vechi: `settings/easeedesk/ticket_types_settings`, `get_type_list`,
 * `get_ajax_type_autocomplete`, `update_type`, `delete_type`,
 * `change_type_status`. Toate cu `settings.easeedesk.read`.
 *
 * `create_new_type` nu e aici: ea mobileaza tipul nou (flux, statusuri, pasi,
 * formulare) si sta in bucata ei.
 * ---------------------------------------------------------------------- */

export interface TicketTypeListRow {
  ID: number
  /** Id-ul randului din SCHEMA - cel cerut la stergere. 0 = tipul n-are rand. */
  scheme_id: number
  type_name: string
  short_name: string
  color_identifier: string
  active: boolean
  /** Bifa "Hide Ticket", a furnizorului. */
  hidden: boolean
  /** Fisa n-are ce spune despre tipul asta: butonul Overview o spune de aici. */
  never_used: boolean
  /** Ce ii lipseste: `flow`, `forms`. Gol la tipurile comune. */
  missing: string[]
}

export interface TicketTypeListPage {
  title: string
  moduleUrl: string
  /** Tipurile COMUNE tuturor instantelor: se aprind si se sting, atat. */
  shared: TicketTypeListRow[]
  /** Tipurile ALE INSTANTEI: se si redenumesc, si se sterg. */
  own: TicketTypeListRow[]
}

/** Raspunsul lui `change_type_status` cand tipul nu e gata de publicat. */
export interface TicketTypeConfirm {
  confirm: true
  type: string
  missing: string[]
}

export function ticketTypeListQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_types_settings'],
    queryFn: () => apiGet<TicketTypeListPage>('/settings/easeedesk/ticket_types_settings/list'),
  })
}

export function updateTicketType(
  type: number,
  fields: { type_name?: string; type_color?: string; type_short_name?: string; type_hide: boolean },
) {
  const body: Record<string, string | number> = { type_hide: fields.type_hide ? 1 : 0 }

  /* Numai campurile chiar trimise: o cerere care aduce doar bifa nu trebuie sa
     treaca prin redenumire, fiindca un tip comun ar fi refuzat acolo. */
  if (fields.type_name !== undefined) body.type_name = fields.type_name
  if (fields.type_color !== undefined) body.type_color = fields.type_color
  if (fields.type_short_name !== undefined) body.type_short_name = fields.type_short_name

  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/ticket_types_settings/${type}`, body)
}

export function changeTicketTypeStatus(type: number, active: boolean, confirmed = false) {
  return apiPost<{ ok?: boolean } & Partial<TicketTypeConfirm>>(
    `/settings/easeedesk/ticket_types_settings/${type}/status`,
    { active: active ? 1 : 0, confirmed: confirmed ? 1 : 0 },
  )
}

/** Cere id-ul randului din SCHEMA, nu al tipului. */
export function removeTicketType(scheme: number) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/ticket_types_settings/scheme/${scheme}/remove`, {})
}

/** Ce s-a apucat sa scrie `TicketTypeStarter` la nasterea tipului. */
export interface TicketTypeBuilt {
  workflow: number | null
  statuses: number[]
  steps: number
  forms: number
  /** Se poate INCHIDE un tichet de tipul asta? Numai un status final inchide. */
  closable: boolean
}

export function createTicketType(fields: {
  type_name: string
  type_color: string
  type_short_name: string
  type_hide: boolean
}) {
  return apiPost<{ ok?: boolean; ID?: number; built?: TicketTypeBuilt; furnished?: boolean }>(
    '/settings/easeedesk/ticket_types_settings',
    { ...fields, type_hide: fields.type_hide ? 1 : 0 },
  )
}

/* -------------------------------------------------------------------------
 * Un tip de tichet, facut uitandu-te la el
 *
 * Perechile vechi: `settings/easeedesk/build_type_form/{tip}` si
 * `settings/easeedesk/build_type`. Ecranul apare NUMAI pentru un tip gol: unul
 * care are deja flux se schimba din fisa lui, pas cu pas.
 * ---------------------------------------------------------------------- */

/** O piesa din paleta: camp gata facut sau camp propriu instantei. */
export interface BuildPiece {
  id: number
  /** 1 = al instantei ("yours"), 0 = din setul comun. */
  custom: number
  name: string
  /** Felul, scris pe intelesul omului: "text", "several from a list", ... */
  type: string
}

export interface BuildPhase {
  name: string
  /** 1 in lucru, 2 rezolvat, 3 se termina aici, 4 aprobat, 5 respins. */
  kind: number
  color: string
  fields: { id: number; custom: boolean }[]
}

export interface BuildTypePage {
  title: string
  moduleUrl: string
  type: { ID: number; type_name: string; short_name: string; color_identifier: string }
  pieces: BuildPiece[]
  /** [numele statusului, cu litere mici] => felul lui. */
  known: Record<string, number>
  /** `false` cand tipul are deja flux: atunci se schimba din fisa, nu de aici. */
  buildable: boolean
}

export function buildTypeFormQuery(type: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'build_type_form', type],
    queryFn: () => apiGet<BuildTypePage>(`/settings/easeedesk/build_type_form/${type}`),
  })
}

export function buildTicketType(type: number, phases: BuildPhase[]) {
  /* JSON, nu campuri de formular: fiecare faza are o lista de piese, deci un
     tablou in tablou. */
  return apiPostJson<{ ok?: boolean; built?: TicketTypeBuilt & { fields: number }; closable?: boolean }>(
    `/settings/easeedesk/build_type/${type}`,
    { phases },
  )
}
